import { Box, Button, Chip, Paper, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { OpenInNew } from '@mui/icons-material';
import type { Theme } from '@mui/material/styles';
import {
	Bar,
	BarChart,
	CartesianGrid,
	Cell,
	Legend,
	ResponsiveContainer,
	Tooltip as RechartsTooltip,
	XAxis,
	YAxis,
} from 'recharts';
import { Util } from '../../util/Util';
import {
	SituationChartCard,
	SituationDetailShell,
	SituationKpiGrid,
	formatChartTooltip,
	formatChartValue,
	formatDetailValue,
	getChartSurface,
	getRechartsTooltipProps,
} from '../client/situationDetailShared';

export const PENDING_DEPOSIT_COLORS = {
	instant: '#2e7d32',
	recovery: '#0288d1',
	mode: '#ed6c02',
	payment: '#2e7d32',
	change: '#0288d1',
	expense: '#c62828',
	refund: '#ad1457',
};

export type PendingModeBreakdown = {
	mode: string;
	amount: number;
	payment_count: number;
};

export type PendingTypeBreakdown = {
	type: string;
	amount: number;
	payment_count: number;
};

export type PendingDepositSummary = {
	total: number;
	income: number;
	expenses: number;
	payment_count: number;
	wallet_count: number;
	wallet_debit_count: number;
	change_count: number;
	expense_count: number;
	refund_count: number;
	mode_count: number;
	by_mode: PendingModeBreakdown[];
	by_type: PendingTypeBreakdown[];
	lines: Array<{ type: string; mode: string; amount: number; payment_count: number }>;
};

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

const typeColor = (type: string) => {
	const key = String(type || '').toLowerCase();
	if (key.includes('wallet')) return PENDING_DEPOSIT_COLORS.mode;
	if (key.includes('change')) return PENDING_DEPOSIT_COLORS.change;
	if (key.includes('expense')) return PENDING_DEPOSIT_COLORS.expense;
	if (key.includes('refund')) return PENDING_DEPOSIT_COLORS.refund;
	if (key.includes('instant') || key.includes('payment')) return PENDING_DEPOSIT_COLORS.payment;
	return PENDING_DEPOSIT_COLORS.recovery;
};

export const parsePendingDeposit = (row: {
	date?: string;
	total?: number;
	income?: number;
	expenses?: number;
	payment_count?: number;
	wallet_count?: number;
	wallet_debit_count?: number;
	change_count?: number;
	expense_count?: number;
	refund_count?: number;
	by_mode?: PendingModeBreakdown[];
	by_type?: PendingTypeBreakdown[];
}): PendingDepositSummary => {
	const by_type = (row.by_type || []).map((item) => ({
		type: item.type,
		amount: Number(item.amount) || 0,
		payment_count: Number(item.payment_count) || 0,
	}));
	const by_mode = (row.by_mode || []).map((item) => ({
		mode: item.mode,
		amount: Number(item.amount) || 0,
		payment_count: Number(item.payment_count) || 0,
	}));
	return {
		total: Number(row.total) || 0,
		income: Number(row.income) || 0,
		expenses: Number(row.expenses) || 0,
		payment_count: Number(row.payment_count) || 0,
		wallet_count: Number(row.wallet_count) || 0,
		wallet_debit_count: Number(row.wallet_debit_count) || 0,
		change_count: Number(row.change_count) || 0,
		expense_count: Number(row.expense_count) || 0,
		refund_count: Number(row.refund_count) || 0,
		mode_count: by_mode.length,
		by_mode,
		by_type,
		lines: by_type.map((item) => ({
			type: item.type,
			mode: item.type,
			amount: item.amount,
			payment_count: item.payment_count,
		})),
	};
};

const PendingBarChart = ({
	data,
	dataKey,
	nameKey,
	barName,
	color,
	theme,
	layout = 'horizontal',
}: {
	data: Array<Record<string, string | number>>;
	dataKey: string;
	nameKey: string;
	barName: string;
	color: string | ((entry: Record<string, string | number>) => string);
	theme: Theme;
	layout?: 'horizontal' | 'vertical';
}) => {
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const chartSurface = getChartSurface(theme);
	const rechartsTooltipProps = getRechartsTooltipProps(theme);
	const tickSize = isMobile ? 9 : 10;
	const chartHeight = layout === 'vertical'
		? Math.max(isMobile ? 200 : 220, data.length * (isMobile ? 48 : 40))
		: isMobile ? 240 : 220;

	if (data.length === 0) {
		return (
			<Typography variant="body2" color="text.secondary" fontStyle="italic" sx={{ py: 3, textAlign: 'center' }}>
				—
			</Typography>
		);
	}

	return (
		<ResponsiveContainer width="100%" height={chartHeight}>
			{layout === 'vertical' ? (
				<BarChart layout="vertical" data={data} margin={{ top: 4, right: isMobile ? 8 : 16, left: 4, bottom: 4 }}>
					<CartesianGrid strokeDasharray="3 3" stroke={chartSurface.grid} horizontal={false} />
					<XAxis type="number" tickFormatter={formatChartValue} tick={{ fontSize: tickSize, fill: chartSurface.tick }} stroke={chartSurface.axis} />
					<YAxis
						type="category"
						dataKey={nameKey}
						tick={{ fontSize: tickSize, fill: chartSurface.tick }}
						stroke={chartSurface.axis}
						width={isMobile ? 72 : 96}
					/>
					<RechartsTooltip {...rechartsTooltipProps} formatter={(value: number) => formatChartTooltip(value)} />
					<Bar dataKey={dataKey} name={barName} radius={[0, 4, 4, 0]} barSize={isMobile ? 14 : 18}>
						{data.map((entry, index) => (
							<Cell
								key={`${entry[nameKey]}-${index}`}
								fill={typeof color === 'function' ? color(entry) : color}
							/>
						))}
					</Bar>
				</BarChart>
			) : (
				<BarChart data={data} margin={{ top: 8, right: 12, left: 4, bottom: isMobile ? 40 : 8 }}>
					<CartesianGrid strokeDasharray="3 3" stroke={chartSurface.grid} vertical={false} />
					<XAxis
						dataKey={nameKey}
						tick={{ fontSize: tickSize, fill: chartSurface.tick }}
						stroke={chartSurface.axis}
						interval={0}
						angle={isMobile ? -25 : 0}
						textAnchor={isMobile ? 'end' : 'middle'}
						height={isMobile ? 48 : 30}
					/>
					<YAxis tickFormatter={formatChartValue} tick={{ fontSize: tickSize - 1, fill: chartSurface.tick }} stroke={chartSurface.axis} width={isMobile ? 40 : 48} />
					<RechartsTooltip {...rechartsTooltipProps} formatter={(value: number) => formatChartTooltip(value)} />
					<Legend wrapperStyle={{ fontSize: isMobile ? 9 : 11, color: chartSurface.legend }} />
					<Bar dataKey={dataKey} name={barName} radius={[4, 4, 0, 0]} barSize={isMobile ? 18 : 24}>
						{data.map((entry, index) => (
							<Cell
								key={`${entry[nameKey]}-${index}`}
								fill={typeof color === 'function' ? color(entry) : color}
							/>
						))}
					</Bar>
				</BarChart>
			)}
		</ResponsiveContainer>
	);
};

const PendingBreakdownTable = ({
	lines,
	t,
	theme,
}: {
	lines: PendingDepositSummary['lines'];
	t: TranslateFn;
	theme: Theme;
}) => {
	const chartSurface = getChartSurface(theme);

	if (lines.length === 0) {
		return null;
	}

	return (
		<Paper elevation={0} sx={{ p: { xs: 1.25, md: 2 }, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
			<Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
				{t('common:deposits.pendingDetail.breakdown')}
			</Typography>
			<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
				{lines.map((line) => (
					<Box
						key={`${line.type}-${line.mode}`}
						sx={{
							display: 'grid',
							gridTemplateColumns: { xs: '1fr', sm: '1.2fr 1fr 1fr auto' },
							gap: 1,
							alignItems: 'center',
							p: 1.25,
							borderRadius: 1.5,
							bgcolor: chartSurface.stripeBg,
							border: '1px solid',
							borderColor: 'divider',
						}}
					>
						<Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
							<Chip
								size="small"
								label={line.type}
								sx={{ bgcolor: typeColor(line.type), color: '#fff', fontWeight: 700 }}
							/>
							<Typography variant="body2" fontWeight={600}>{line.mode}</Typography>
						</Stack>
						<Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
							{t('common:deposits.pendingDetail.payments', { count: line.payment_count })}
						</Typography>
						<Typography variant="body2" fontWeight={700} sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
							{formatDetailValue(line.amount)}
						</Typography>
						<Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'block', sm: 'none' } }}>
							{t('common:deposits.pendingDetail.payments', { count: line.payment_count })}
						</Typography>
					</Box>
				))}
			</Box>
		</Paper>
	);
};

export const PendingDepositDetailPanel = ({
	row,
	t,
	onOpen,
	canOpen = false,
}: {
	row: {
		date?: string;
		total?: number;
		data?: Record<string, Array<{ mode: string; amount: number; payment_count?: number }>>;
		payment_count?: number;
		by_mode?: PendingModeBreakdown[];
		by_type?: PendingTypeBreakdown[];
	};
	t: TranslateFn;
	onOpen?: () => void;
	canOpen?: boolean;
}) => {
	const theme = useTheme();
	const chartSurface = getChartSurface(theme);
	const summary = parsePendingDeposit(row);

	const typeChartData = summary.by_type.map((item) => ({
		type: item.type,
		amount: item.amount,
	}));
	const modeChartData = summary.by_mode.map((item) => ({
		mode: item.mode,
		amount: item.amount,
	}));

	return (
		<SituationDetailShell theme={theme}>
			<Paper elevation={0} sx={{ p: { xs: 1.5, md: 2 }, mb: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
				<Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1.5}>
					<Box>
						<Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
							{row.date ? Util.formatDate(row.date, 'full') : '—'}
						</Typography>
						<Typography variant="body2" color="text.secondary">
							{t('common:deposits.pendingDetail.subtitle')}
						</Typography>
					</Box>
					<Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
						<Chip
							label={formatDetailValue(summary.total)}
							color="warning"
							sx={{ fontWeight: 800, fontSize: '0.95rem', px: 0.5 }}
						/>
						{canOpen && onOpen && (
							<Button
								variant="contained"
								color="success"
								size="small"
								startIcon={<OpenInNew />}
								onClick={onOpen}
							>
								{t('common:ui.buttons.open')}
							</Button>
						)}
					</Stack>
				</Stack>
			</Paper>

			<SituationKpiGrid
				theme={theme}
				stats={[
					{ label: t('common:deposits.pendingDetail.totalPending'), value: summary.total, signed: true },
					{ label: t('common:deposits.pendingDetail.income'), value: summary.income, signed: false },
					{ label: t('common:deposits.pendingDetail.expenses'), value: summary.expenses, signed: false },
					{ label: t('common:deposits.pendingDetail.paymentCount'), value: summary.payment_count, signed: false },
					{ label: t('common:deposits.pendingDetail.walletCount'), value: summary.wallet_count, signed: false },
					{ label: t('common:deposits.pendingDetail.walletDebitCount'), value: summary.wallet_debit_count, signed: false },
					{ label: t('common:deposits.pendingDetail.changeCount'), value: summary.change_count, signed: false },
					{ label: t('common:deposits.pendingDetail.expenseCount'), value: summary.expense_count, signed: false },
					{ label: t('common:deposits.pendingDetail.refundCount'), value: summary.refund_count, signed: false },
				]}
			/>

			<Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: { xs: 1.5, md: 2 }, mb: 2 }}>
				<SituationChartCard title={t('common:deposits.pendingDetail.byType')} chartSurface={chartSurface}>
					<PendingBarChart
						data={typeChartData}
						dataKey="amount"
						nameKey="type"
						barName={t('common:tables.amount')}
						color={(entry) => typeColor(String(entry.type))}
						theme={theme}
						layout="horizontal"
					/>
				</SituationChartCard>
				<SituationChartCard title={t('common:deposits.pendingDetail.byMode')} chartSurface={chartSurface}>
					<PendingBarChart
						data={modeChartData}
						dataKey="amount"
						nameKey="mode"
						barName={t('common:tables.amount')}
						color={PENDING_DEPOSIT_COLORS.mode}
						theme={theme}
						layout="vertical"
					/>
				</SituationChartCard>
			</Box>

			<PendingBreakdownTable lines={summary.lines} t={t} theme={theme} />
		</SituationDetailShell>
	);
};

